import { z } from 'zod'

import { moneySchema, percentSchema } from '@/schemas/common'
import { isoDateSchema } from '@/schemas/portfolio'

/**
 * Mutual funds — `/api/v5/mutual-funds/`, in the app's own vocabulary.
 *
 * Wire shapes live in `@/api/schema` and are never restated here; this file
 * describes what the app holds AFTER `fundService`'s mappers have run, so
 * every name is camelCase and every decimal is a branded `Money`.
 *
 * Four rules the whole file turns on, each verified against the live API
 * (dev.mmitra.space, 2026-09-26):
 *
 *  1. THESE ARE MONTHLY FILINGS, NOT MARKET DATA. Every analytics read is
 *     taken over one holdings PERIOD — a Bikram Sambat month label such as
 *     "Bhadra 2083", the exact string `/periods/` lists. An unknown label is
 *     not an error: the backend answers 200 with zeros, an empty list and
 *     `period: null`, which is why `period` is nullable on every payload that
 *     echoes it. Treat `period: null` + empty as "no filings for that month".
 *  2. RUPEES ARE DECIMAL STRINGS (`moneySchema`), and nearly every one is
 *     nullable. A null value is "not reported", never zero — a table cell
 *     draws "—", not "0.00 Cr".
 *  3. A SECTOR HAS A CODE AND A NAME, and they are not interchangeable:
 *     `/industry-holdings/companies/` filters on the CODE (`BANKING`) and
 *     answers `[]` for the display name. One slice has neither — holdings the
 *     backend could not classify — so both are nullable, and that slice
 *     cannot be used as a filter.
 *  4. A FUND'S `name` IS OFTEN NOT A NAME. It is null, or it repeats the
 *     symbol (`KSLY` / `KSLY`). The data keeps what was sent (blank strings
 *     aside); whether to draw a subtitle is `fundSubtitle`'s call
 *     (`features/mutual-funds/lib/format.ts`).
 */

// ── Vocabulary ───────────────────────────────────────────────────────────────

/**
 * The directory's `category` filter — the three Price vs NAV tabs.
 * Matured is a STATUS, not a third fund type: every matured fund on the wire
 * so far is `CLOSE_ENDED`.
 */
export const FUND_CATEGORIES = ['close-ended', 'open-ended', 'matured'] as const
export const fundCategorySchema = z.enum(FUND_CATEGORIES)

/**
 * Closed on purpose, although the OpenAPI document types both fields as a
 * bare `string` (the serializer is a CharField, not a ChoiceField). A value
 * neither branch names — one `INTERVAL` fund type has been seen on the wire —
 * is mapped to `null` in `fundService.mapFund` before it ever reaches this
 * schema, rather than parsed against the closed union: nothing on screen
 * branches on either field except display, so an unrecognised value has no
 * reason to blank the whole page.
 */
export const fundTypeSchema = z.enum(['CLOSE_ENDED', 'OPEN_ENDED'])
export const fundStatusSchema = z.enum(['ACTIVE', 'MATURED'])

/** A fund symbol, a stock symbol or a sector code: never blank. */
const codeSchema = z.string().min(1)

/** A name the backend may not have — see rule 4 above. */
const optionalNameSchema = z.string().min(1).nullable()

/**
 * The holdings period a payload was taken over. Null when the label asked
 * for matched no filings (rule 1).
 */
const periodLabelSchema = z.string().min(1).nullable()

// ── /periods/ ────────────────────────────────────────────────────────────────

/**
 * One selectable holdings period. `label` is the value every `period` query
 * parameter takes, verbatim; `month`/`year` are the same label split, for a
 * picker that wants to group by year. The list arrives NEWEST FIRST and is
 * not contiguous — months with no filings are simply absent.
 */
export const fundPeriodSchema = z.object({
  label: z.string().min(1),
  month: z.string().min(1),
  year: z.number().int(),
})

// ── / (the fund directory) ───────────────────────────────────────────────────

/**
 * One fund from the directory — Price vs NAV's rows.
 *
 * `aum` IS ON THE WIRE AND IS DELIBERATELY NOT MAPPED: its unit disagrees with
 * every other rupee figure in the feature (NIBLSF `aum` "11409684.01" against
 * a holdings value of "8722529778.30" — it reads as thousands). A field that
 * exists is a field the next screen renders, so it is left off until the
 * backend states its unit. For a fund's size in rupees use the analytics
 * `value` (`FundStockCount.value`), which agrees with the holdings.
 *
 * `marketPrice` and `premiumDiscountPercent` are set only when `isListed`;
 * an unlisted fund has no market to be at a premium to. Premium is positive
 * (price above NAV), discount negative.
 */
export const fundSchema = z.object({
  symbol: codeSchema,
  name: optionalNameSchema,
  fundType: fundTypeSchema.nullable(),
  status: fundStatusSchema.nullable(),
  fundManager: optionalNameSchema,
  /** A URL, when the backend has one. None do today. */
  fundManagerLogo: z.string().min(1).nullable(),
  /** The fund's authorised size in rupees, as the scheme was registered. */
  fundSize: moneySchema.nullable(),
  /** Rupees per unit. Null for a matured fund. */
  nav: moneySchema.nullable(),
  /** The month the NAV was reported for — a period label. */
  navPeriod: periodLabelSchema,
  maturityDate: isoDateSchema.nullable(),
  /** The scheme's term in years; null for an open-ended fund, which has none. */
  maturityYears: z.number().int().min(0).nullable(),
  startDate: isoDateSchema.nullable(),
  /** The NEPSE index on `startDate` — what "since launch" is measured against. */
  nepseIndexAtStart: moneySchema.nullable(),
  isListed: z.boolean(),
  marketPrice: moneySchema.nullable(),
  premiumDiscountPercent: percentSchema.nullable(),
})

// ── /analytics/* ─────────────────────────────────────────────────────────────

/** The three headline tiles over one period. */
export const fundAnalyticsSummarySchema = z.object({
  period: periodLabelSchema,
  fundsTracked: z.number().int().min(0),
  uniqueStocks: z.number().int().min(0),
  /** A count of SHARES (units of stock), not rupees. */
  totalShares: z.number().int().min(0),
  /**
   * PROPOSED (mf:an, `mm-mf-2026-10-29/asks/analytics-summary.md`): rupees the funds bought, net
   * of what they sold, since the previous filing. Not served yet — null until mm-be adds
   * `net_bought_value`; nothing reads it while `FUND_NET_BOUGHT_AVAILABLE` is false.
   */
  netBoughtValue: moneySchema.nullable(),
})

/**
 * A fund and how many distinct stocks it held — Analytics' "Fund stock count"
 * tab and the whole of Fund rankings. `value` is the market value of those
 * holdings in rupees: the figure to use wherever a screen means "portfolio
 * value" or "AUM" (never `FundRow.aum` — see `fundSchema`).
 */
export const fundStockCountSchema = z.object({
  symbol: codeSchema,
  name: optionalNameSchema,
  stockCount: z.number().int().min(0),
  value: moneySchema.nullable(),
})

/** A fund and how many sectors it spans — Analytics' "Sector diversity" tab. */
export const fundSectorCountSchema = z.object({
  symbol: codeSchema,
  name: optionalNameSchema,
  sectorCount: z.number().int().min(0),
  value: moneySchema.nullable(),
})

/**
 * A stock and how many funds hold it — Analytics' "Most popular" tab.
 * `sector` is the sector's DISPLAY NAME here, not its code, and is null for a
 * quarter of the rows today (5 of 20): an unclassified stock, drawn as "—".
 */
export const popularStockSchema = z.object({
  symbol: codeSchema,
  sector: optionalNameSchema,
  fundCount: z.number().int().min(0),
  value: moneySchema.nullable(),
})

/**
 * One slice of a sector split — the industry donut, and one fund's own split.
 *
 * `sector` (the code) and `name` are null TOGETHER on the one slice the
 * backend could not classify (4.25% of all holdings today). The data keeps
 * the nulls; labelling that slice ("Unclassified") and refusing to offer it
 * as a companies filter — it has no code to filter on — is the screen's job.
 */
export const sectorSliceSchema = z.object({
  sector: codeSchema.nullable(),
  name: optionalNameSchema,
  stockCount: z.number().int().min(0),
  value: moneySchema.nullable(),
  /** Share of the whole split's value, "27.63" meaning 27.63 %. */
  pct: percentSchema.nullable(),
})

/**
 * One fund's value across its sectors — the Sector diversity dialog.
 *
 * An unknown fund is NOT a 404: the backend answers 200 with `value: null`
 * and no sectors. That is the empty state, not an error.
 */
export const fundSectorBreakdownSchema = z.object({
  fund: codeSchema,
  value: moneySchema.nullable(),
  sectors: z.array(sectorSliceSchema),
})

/**
 * Stocks held per month, per fund — the Monthly trends line chart.
 *
 * `periods` labels the x axis and is NOT CONTIGUOUS (Falgun 2081 is followed
 * by Asadh 2083): draw the axis from it, never synthesise the months between.
 * Each series' `data` lines up with `periods` index for index — the schema
 * refuses a payload where it does not, since a chart drawn from a
 * misaligned series would put every point in the wrong month.
 *
 * A point is null where the fund filed nothing that month (the service maps
 * the wire's 0 — see `mapTrends`). Draw a gap, not a drop to zero.
 */
export const fundTrendSeriesSchema = z.object({
  symbol: codeSchema,
  data: z.array(z.number().int().min(1).nullable()),
})

export const fundTrendsSchema = z
  .object({
    periods: z.array(z.string().min(1)),
    series: z.array(fundTrendSeriesSchema),
  })
  .refine(
    (trends) => trends.series.every((series) => series.data.length === trends.periods.length),
    'Every trend series must have one point per period',
  )

// ── /industry-holdings/* ─────────────────────────────────────────────────────

/** Where all funds together are invested, by sector, over one period. */
export const industryHoldingsSchema = z.object({
  period: periodLabelSchema,
  /** The whole of every fund's holdings, in rupees — what `pct` is a share of. */
  total: moneySchema.nullable(),
  sectors: z.array(sectorSliceSchema),
})

/** One company inside a sector, summed across every fund that holds it. */
export const sectorCompanySchema = z.object({
  symbol: codeSchema,
  name: optionalNameSchema,
  /** How many funds hold it. */
  funds: z.number().int().min(0),
  shares: z.number().int().min(0),
  value: moneySchema.nullable(),
})

// ── /monthly-holdings/ ───────────────────────────────────────────────────────

/** One company's total across every fund, and its share of all holdings. */
export const monthlyHoldingSchema = z.object({
  symbol: codeSchema,
  name: optionalNameSchema,
  /**
   * How many funds hold it (mm-be #372). `null` until that API is live: the Industry table then
   * shows the company's share instead.
   */
  funds: z.number().int().min(0).nullable(),
  shares: z.number().int().min(0),
  value: moneySchema.nullable(),
  pct: percentSchema.nullable(),
})

export const monthlyHoldingsSchema = z.object({
  period: periodLabelSchema,
  total: moneySchema.nullable(),
  companies: z.array(monthlyHoldingSchema),
})

// ── /{fund}/holdings/, /holders/{symbol}/, /{fund}/nav/ (mm-be #372) ─────────

/** One stock a fund holds: its sector, the shares and their value, and its share of the fund. */
export const fundHoldingSchema = z.object({
  symbol: codeSchema,
  name: optionalNameSchema,
  /** The sector CODE, or null for a holding the filings do not tie to one. */
  sector: codeSchema.nullable(),
  sectorName: optionalNameSchema,
  shares: z.number().int().min(0),
  value: moneySchema.nullable(),
  pct: percentSchema.nullable(),
})

/** Every stock one fund holds in a month, biggest first. */
export const fundHoldingsSchema = z.object({
  fund: codeSchema,
  period: periodLabelSchema,
  total: moneySchema.nullable(),
  stockCount: z.number().int().min(0),
  companies: z.array(fundHoldingSchema),
})

/** One fund holding a stock, and its share of what every fund holds of it. */
export const stockHolderSchema = z.object({
  symbol: codeSchema,
  name: optionalNameSchema,
  shares: z.number().int().min(0),
  value: moneySchema.nullable(),
  pct: percentSchema.nullable(),
})

/** Every fund holding one stock in a month, biggest first. */
export const stockHoldersSchema = z.object({
  symbol: codeSchema,
  name: optionalNameSchema,
  period: periodLabelSchema,
  shares: z.number().int().min(0),
  value: moneySchema.nullable(),
  funds: z.array(stockHolderSchema),
})

/** One monthly NAV filing: NAV per unit, net assets and units in issue. */
export const navPointSchema = z.object({
  period: z.string().min(1),
  nav: moneySchema.nullable(),
  netAssets: moneySchema.nullable(),
  units: moneySchema.nullable(),
})

/** A fund's last monthly NAV filings, newest first (a month not filed is left out). */
export const navHistorySchema = z.object({
  fund: codeSchema,
  points: z.array(navPointSchema),
})

// ── /{fund}/income-statement/ (mm-be #376) ───────────────────────────────────

/** One line of a fund's income or expenses: its key, the report's own label, its rupees. */
export const incomeLineSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  value: moneySchema.nullable(),
})

/** One fiscal year's statement, read off its year-end (Asadh) report. Amounts in rupees. */
export const incomeYearSchema = z.object({
  fiscalYear: z.string().min(1),
  period: z.string(),
  income: z.array(incomeLineSchema),
  totalIncome: moneySchema.nullable(),
  expenses: z.array(incomeLineSchema),
  totalExpenses: moneySchema.nullable(),
  netProfit: moneySchema.nullable(),
  netProfitPerUnit: moneySchema.nullable(),
  units: moneySchema.nullable(),
})

/** Up to five fiscal years, newest first; none published is `years: []`. */
export const fundIncomeStatementSchema = z.object({
  fund: codeSchema,
  years: z.array(incomeYearSchema),
})

// ── /dividends/ ──────────────────────────────────────────────────────────────

/**
 * A close-ended fund's distributable dividend for one month. The list is
 * EMPTY today, so `dividend`'s unit (percent or rupees) has never been seen on
 * the wire; it is a decimal either way, and the screen must not guess a unit
 * it cannot show.
 */
export const distributableDividendSchema = z.object({
  symbol: codeSchema,
  name: optionalNameSchema,
  year: z.number().int(),
  month: optionalNameSchema,
  dividend: moneySchema.nullable(),
})

/** An open-ended fund's dividend record: all-time and per year, as percents. */
export const openEndedDividendSchema = z.object({
  symbol: codeSchema,
  name: optionalNameSchema,
  totalPercent: percentSchema.nullable(),
  perYearPercent: percentSchema.nullable(),
})

export const fundDividendsSchema = z.object({
  distributable: z.array(distributableDividendSchema),
  openEnded: z.array(openEndedDividendSchema),
})

// ── What the reads take ──────────────────────────────────────────────────────

/**
 * The directory's filter. Optional: no `category` is every fund (71 today).
 * The wire also takes a `q` search param, unused here — nothing in the app
 * searches the directory by symbol or name yet.
 */
export const fundListQuerySchema = z.object({
  category: fundCategorySchema.optional(),
})

/**
 * The trends chart's request. `funds` is required in practice: with none,
 * the backend answers every fund it has (51 series), which no chart can draw.
 * The wire also takes a `months` window (1–24, 12 when absent), unused here —
 * no control in the app asks for anything but the default.
 */
export const fundTrendsQuerySchema = z.object({
  funds: z.array(codeSchema).min(1),
})

export type FundCategory = z.infer<typeof fundCategorySchema>
export type FundType = z.infer<typeof fundTypeSchema>
export type FundStatus = z.infer<typeof fundStatusSchema>
export type FundPeriod = z.infer<typeof fundPeriodSchema>
export type Fund = z.infer<typeof fundSchema>
export type FundAnalyticsSummary = z.infer<typeof fundAnalyticsSummarySchema>
export type FundStockCount = z.infer<typeof fundStockCountSchema>
export type FundSectorCount = z.infer<typeof fundSectorCountSchema>
export type PopularStock = z.infer<typeof popularStockSchema>
export type SectorSlice = z.infer<typeof sectorSliceSchema>
export type FundSectorBreakdown = z.infer<typeof fundSectorBreakdownSchema>
export type FundTrendSeries = z.infer<typeof fundTrendSeriesSchema>
export type FundTrends = z.infer<typeof fundTrendsSchema>
export type IndustryHoldings = z.infer<typeof industryHoldingsSchema>
export type SectorCompany = z.infer<typeof sectorCompanySchema>
export type MonthlyHolding = z.infer<typeof monthlyHoldingSchema>
export type MonthlyHoldings = z.infer<typeof monthlyHoldingsSchema>
export type FundHolding = z.infer<typeof fundHoldingSchema>
export type FundHoldings = z.infer<typeof fundHoldingsSchema>
export type StockHolder = z.infer<typeof stockHolderSchema>
export type StockHolders = z.infer<typeof stockHoldersSchema>
export type NavPoint = z.infer<typeof navPointSchema>
export type NavHistory = z.infer<typeof navHistorySchema>
export type IncomeLine = z.infer<typeof incomeLineSchema>
export type IncomeYear = z.infer<typeof incomeYearSchema>
export type FundIncomeStatement = z.infer<typeof fundIncomeStatementSchema>
export type DistributableDividend = z.infer<typeof distributableDividendSchema>
export type OpenEndedDividend = z.infer<typeof openEndedDividendSchema>
export type FundDividends = z.infer<typeof fundDividendsSchema>
export type FundListQuery = z.infer<typeof fundListQuerySchema>
export type FundTrendsQuery = z.infer<typeof fundTrendsQuerySchema>
